import { useEffect, useState } from 'react';
import { useVsjFlow } from './vsj-flow';
import { fetchPlaceValueNear } from '../place-value-api';
import type { PlaceValueAvailable } from '../place-value-api';
import { PROPERTY_TYPE_LABEL } from './vsj-mocks';
import { VsjStepHeader } from './VsjStepHeader';
import { VsjSheet } from './VsjSheet';
import {
  IconPin, IconReport, ConfidenceBars,
} from './vsj-icons';
import {
  fetchValuationResult,
  createValuationReport,
  toVsjResult,
  buildResultFactors,
  fmtPrice,
  BACKEND_TYPE_DISPLAY,
  FACTOR_ICONS,
} from './vsj-result-api';
import type { VsjResultData, ValuationResultDto } from './vsj-result-api';

// ── Constants ─────────────────────────────────────────────────────────────────

const VSJ_SLUG = 'valor-san-juan';

const CONFIDENCE_NOTE: Record<1 | 2 | 3, string> = {
  3: 'El área tiene buena cobertura de datos. El rango es confiable como referencia.',
  2: 'Hay referencias en el área, aunque con menos datos de los que nos gustaría.',
  1: 'Pocos datos disponibles en este entorno. Tomá el rango como orientación general.',
};

const PROPERTY_TYPE_BACKEND: Record<string, string> = {
  casa:         'house',
  departamento: 'apartment',
  terreno:      'lot',
};

type FetchStatus = 'loading' | 'available' | 'insufficient_data' | 'error';
type SheetKind   = 'save' | null;

// ── Component ─────────────────────────────────────────────────────────────────

export function VsjResultStep(): JSX.Element {
  const { place, property, goTo, readingResult, sessionToken, reportToken, setReportToken, locateResult } = useVsjFlow();

  const [sheet,         setSheet]         = useState<SheetKind>(null);
  const [status,        setStatus]        = useState<FetchStatus>('loading');
  const [result,        setResult]        = useState<VsjResultData | null>(null);
  const [sessionResult, setSessionResult] = useState<ValuationResultDto | null>(null);
  const [saving,        setSaving]        = useState(false);
  const [saveError,     setSaveError]     = useState(false);
  const [copied,        setCopied]        = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchLegacy(): Promise<void> {
      const backendType = PROPERTY_TYPE_BACKEND[property.type] ?? property.type;
      const response = await fetchPlaceValueNear(VSJ_SLUG, place.lng, place.lat, {
        propertyType: backendType,
        operation:    'sale',
        currency:     'USD',
      });
      if (cancelled) return;
      if (response.status === 'insufficient_data') { setStatus('insufficient_data'); return; }
      const group = (response as PlaceValueAvailable).market.groups[0];
      if (!group) { setStatus('insufficient_data'); return; }
      setResult(toVsjResult(group, property.surfaceM2 ?? 100, response.place.contextRadiusM));
      setStatus('available');
    }

    async function load(): Promise<void> {
      if (sessionToken) {
        try {
          const resultData = await fetchValuationResult(sessionToken);
          if (cancelled) return;
          setSessionResult(resultData);
          if (resultData.placeValue.status === 'insufficient_data') {
            setStatus('insufficient_data');
            return;
          }
          const available = resultData.placeValue as PlaceValueAvailable;
          const group = available.market.groups[0];
          if (!group) { setStatus('insufficient_data'); return; }
          const surface =
            resultData.session.areaBuiltM2 ??
            resultData.session.areaLandM2 ??
            property.surfaceM2 ??
            100;
          setResult(toVsjResult(group, surface, available.place.contextRadiusM));
          setStatus('available');
        } catch {
          if (!cancelled) {
            // B9 failed — fall back to coordinate-based fetch
            try {
              await fetchLegacy();
            } catch {
              if (!cancelled) setStatus('error');
            }
          }
        }
      } else {
        try {
          await fetchLegacy();
        } catch {
          if (!cancelled) setStatus('error');
        }
      }
    }

    void load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Once: place + property are final when this step mounts.

  // ── Save ────────────────────────────────────────────────────────────────────

  async function handleSave(): Promise<void> {
    if (reportToken) {
      setSaveError(false);
      setSheet('save');
      return;
    }
    if (!sessionToken) {
      setSheet('save');
      return;
    }
    setSaving(true);
    setSaveError(false);
    setSheet('save'); // open immediately — loader lives inside the sheet
    try {
      const { reportToken: newToken } = await createValuationReport(sessionToken);
      setReportToken(newToken);
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  }

  async function handleCopyLink(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* silent — user can copy manually */
    }
  }

  // ── Derived display values ──────────────────────────────────────────────────

  const effectiveTypeLabel =
    sessionResult
      ? (BACKEND_TYPE_DISPLAY[sessionResult.session.propertyType] ?? property.type)
      : (PROPERTY_TYPE_LABEL[property.type] ?? property.type);

  const effectiveSurface =
    sessionResult
      ? (sessionResult.session.areaBuiltM2 ?? sessionResult.session.areaLandM2 ?? property.surfaceM2 ?? 0)
      : (property.surfaceM2 ?? 0);

  const effectiveStreetLabel =
    sessionResult?.streetTypeLabel ?? readingResult?.streetTypeLabel ?? null;

  const effectivePropertyType =
    sessionResult?.session.propertyType ?? property.type;

  const effectiveLocationText =
    sessionResult?.session.streetLabel
      ?? locateResult?.streetName
      ?? locateResult?.referenceLabel
      ?? place.address;

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (status === 'loading') {
    return (
      <div className="vsj-step vsj-result-step">
        <VsjStepHeader step={4} onBack={() => goTo('property')} />
        <div className="vsj-result-loading">
          <div className="vsj-result-loading-pulse" />
          <p className="vsj-result-loading-label">Buscando referencias…</p>
        </div>
      </div>
    );
  }

  // ── Insufficient data / Error ────────────────────────────────────────────────

  if (status === 'insufficient_data' || status === 'error') {
    const isError = status === 'error';
    return (
      <div className="vsj-step vsj-result-step">
        <VsjStepHeader step={4} onBack={() => goTo('property')} />
        <div className="vsj-result-body vsj-result-unavailable">
          <div className="vsj-result-location-chip vsj-reveal">
            <IconPin size={15} />
            {effectiveLocationText}
          </div>
          <div className="vsj-unavailable-content vsj-reveal" style={{ animationDelay: '80ms' }}>
            <h2 className="vsj-unavailable-title">
              {isError
                ? 'No pudimos obtener la referencia en este momento'
                : 'Todavía no tenemos una referencia confiable para este lugar'}
            </h2>
            <p className="vsj-unavailable-body">
              {isError
                ? 'Hubo un problema al consultar los datos. Intentá de nuevo o probá con otra dirección.'
                : 'Encontramos la ubicación, pero aún no hay suficientes referencias públicas recientes para estimar un rango con responsabilidad en este entorno.'}
            </p>
          </div>
          <div className="vsj-result-actions vsj-reveal" style={{ animationDelay: '160ms' }}>
            <button className="vsj-btn-primary" onClick={() => goTo('home')}>
              Leer otro lugar
            </button>
            <button className="vsj-btn-ghost" onClick={() => goTo('property')}>
              Volver y ajustar datos
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Available ────────────────────────────────────────────────────────────────

  if (!result) return <></>;

  const periodLabel   = readingResult?.periodLabel ?? 'los últimos meses';
  const resultFactors = buildResultFactors(effectiveStreetLabel, result, effectivePropertyType);

  const shareUrl = reportToken
    ? `${window.location.origin}/p/valor-san-juan/informe/${reportToken}`
    : null;

  let reveal = 0;
  const revealStyle = () => ({ animationDelay: `${(reveal++) * 90}ms` });

  return (
    <div className="vsj-step vsj-result-step">
      <VsjStepHeader step={4} onBack={() => goTo('property')} />

      <div className="vsj-result-body">
        {/* 1 · Where we are */}
        <div className="vsj-result-location-chip vsj-reveal" style={revealStyle()}>
          <IconPin size={15} />
          {effectiveLocationText}
        </div>

        {/* 2 · Evidence first */}
        <section className="vsj-result-evidence vsj-reveal" style={revealStyle()}>
          <p className="vsj-section-label">Lo que encontramos</p>
          <p className="vsj-result-observation">
            <strong>{result.observationCount} referencias</strong> de {periodLabel} a menos
            de {result.radiusM} metros, con características similares.
          </p>
          <div className="vsj-result-factors">
            {resultFactors.map((f) => {
              const Icon = FACTOR_ICONS[f.icon];
              return (
                <div key={f.label} className="vsj-result-factor-row">
                  <span className="vsj-result-factor-icon"><Icon size={17} /></span>
                  {f.label}
                </div>
              );
            })}
          </div>
        </section>

        {/* 3 · Bridge from place to property */}
        <p className="vsj-result-leadin vsj-reveal" style={revealStyle()}>
          Con eso, para {effectiveTypeLabel} de {effectiveSurface} m² en este entorno:
        </p>

        {/* 4 · The range */}
        <div className="vsj-result-range-block vsj-reveal" style={revealStyle()}>
          <div className="vsj-range-header">
            <span className="vsj-range-label">Rango de referencia</span>
            <span className="vsj-range-confidence">
              <ConfidenceBars level={result.confidenceLevel} />
              {result.confidenceLabel}
            </span>
          </div>
          <div className="vsj-range-value">
            <span className="vsj-range-currency">{result.currency}</span>
            {fmtPrice(result.totalLow)}
            <span className="vsj-range-sep"> — </span>
            {fmtPrice(result.totalHigh)}
          </div>
          {result.pricePerM2Low !== null && result.pricePerM2High !== null && (
            <p className="vsj-range-per-m2">
              {result.currency} {fmtPrice(result.pricePerM2Low)} — {fmtPrice(result.pricePerM2High)} por m²
            </p>
          )}
        </div>

        {/* 4b · Confidence note */}
        <p className="vsj-confidence-note vsj-reveal" style={revealStyle()}>
          {CONFIDENCE_NOTE[result.confidenceLevel as 1 | 2 | 3]}
        </p>

        {/* 5 · Honesty */}
        <p className="vsj-result-honesty vsj-reveal" style={revealStyle()}>
          Este rango refleja lo que el entorno y las referencias disponibles nos dejan ver.
          No conoce el estado interior, las reformas ni la historia de tu propiedad —
          eso puede mover el valor dentro del rango, o fuera de él.
        </p>

        <p className="vsj-disclaimer vsj-reveal" style={revealStyle()}>
          Referencia orientativa. No reemplaza una tasación profesional.
        </p>

        {/* 6 · Next steps */}
        <div className="vsj-result-actions vsj-reveal" style={revealStyle()}>
          <button
            className="vsj-btn-outline"
            onClick={() => void handleSave()}
            disabled={saving}
            aria-disabled={saving}
          >
            <IconReport size={18} />
            Guardar esta lectura
          </button>
          <button className="vsj-btn-text vsj-btn-text--muted" onClick={() => goTo('home')}>
            Leer otro lugar
          </button>
        </div>
      </div>

      {/* Save sheet */}
      <VsjSheet
        open={sheet === 'save'}
        title={saving ? 'Guardando lectura…' : shareUrl ? 'Tu informe está listo' : 'Guardar esta lectura'}
        onClose={() => setSheet(null)}
        hideCta={!!shareUrl || saving}
      >
        {shareUrl ? (
          <>
            <p>
              Guardá este link para volver al informe desde cualquier dispositivo.
            </p>
            <input
              className="vsj-share-url"
              readOnly
              value={shareUrl}
              onFocus={(e) => e.target.select()}
              aria-label="Link del informe"
            />
            <button
              className="vsj-btn-primary"
              onClick={() => void handleCopyLink(shareUrl)}
            >
              {copied ? 'Copiado' : 'Copiar link'}
            </button>
            <button
              className="vsj-btn-text vsj-btn-text--muted"
              onClick={() => setSheet(null)}
            >
              Cerrar
            </button>
          </>
        ) : saving ? (
          <p className="vsj-sheet-loading">Generando tu informe de referencia…</p>
        ) : saveError ? (
          <p>
            No se pudo generar el informe. Intentá de nuevo en unos momentos.
          </p>
        ) : !sessionToken ? (
          <p>
            Esta función requiere que la ubicación esté confirmada. Intentá una nueva lectura.
          </p>
        ) : null}
      </VsjSheet>
    </div>
  );
}
